import z from "zod";
import { AgentTypeMetadata } from "../../config/agent-registry";
import { baseAgentConfig } from "../../config/base-agent-config";

export const searchBackends = ["tavily", "google", "duckduckgo"] as const;

const webConfigSchema = baseAgentConfig.extend({
  defaultSearchBackend: z.enum(searchBackends).default("tavily"),
  duckduckgo: z
    .object({
      maxResults: z.coerce.number().int().positive().optional().default(10),
      minContentLength: z.coerce
        .number()
        .int()
        .nonnegative()
        .optional()
        .default(0),
      relevanceThreshold: z.coerce
        .number()
        .min(0)
        .max(1)
        .optional()
        .default(0.5),
    })
    .nullish()
    .default(null),

  google: z
    .object({
      apiKey: z.string(),
      // engineId
      cx: z.string(),

      maxResults: z.coerce.number().int().positive().optional().default(10),
      region: z.string().default("us-en"),
      timeRange: z.string().default("y"),
      minContentLength: z.coerce
        .number()
        .int()
        .nonnegative()
        .optional()
        .default(0),
      relevanceThreshold: z.coerce
        .number()
        .min(0)
        .max(1)
        .optional()
        .default(0.5),
    })
    .nullish()
    .default(null),

  tavily: z
    .object({
      apiKey: z.string(),

      searchDepth: z.enum(["basic", "advanced"]).optional().default("basic"),
      maxResults: z.coerce.number().int().positive().optional().default(10),
    })
    .nullish()
    .default(null),

  csrFetching: z
    .object({
      enabled: z.boolean().optional().default(true),
      defaultWaitTime: z.coerce
        .number()
        .int()
        .min(0)
        .max(10000)
        .optional()
        .default(2000),
      extractText: z.boolean().optional().default(true),
    })
    .nullish()
    .default({
      enabled: true,
      defaultWaitTime: 2000,
      extractText: true,
    }),
});

export const webAgentMetaData: AgentTypeMetadata<typeof webConfigSchema> = {
  name: "Web Search",
  description: "Let your Chat Access the Internet",
  category: "Web",
  icon: "search",
  type: "agent",
  version: "Released",
  ribbon: null,
  status: "Active",
  href: "/admin/ai-configuration/connectors/web",
  // placeholders: {
  //     defaultSearchBackend: "google",
  //     defaultSearchRegion: "us-en",
  //     defaultSearchTimeRange: "y",
  //     defaultSearchDepth: "3",
  //     minContentLength: "200",
  //     defaultRelevanceThreshold: "0.5",
  //     maxResultsPerQuery: "10",
  //     searchRateLimit: "10",
  //     searchConcurrentLimit: "3",
  //     googleSearchApiKey: "Enter Google API key (optional)",
  //     googleSearchEngineId: "Enter Google Search Engine ID (optional)",
  //     bingSearchApiKey: "Enter Bing API key (optional)",
  //   },
  placeholders: {
    defaultSearchBackend: "google",
    "tavily.apiKey": "Tavily API key",
    "tavily.searchDepth": "basic",
    "google.apiKey": "Google API key",
    "google.cx": "Google Custom Search Engine ID",
    "tavily.maxResults": "10",
    "google.maxResults": "10",
    "google.region": "us-en",
    "google.timeRange": "y",
    "google.minContentLength": "0",
    "google.relevanceThreshold": "0.5",
    "duckduckgo.maxResults": "10",
    "duckduckgo.minContentLength": "0",
    "duckduckgo.relevanceThreshold": "0.5",
    "csrFetching.enabled": "true",
    "csrFetching.defaultWaitTime": "2000",
    "csrFetching.extractText": "true",
  },
  configSchema: webConfigSchema,
  sensitiveKeys: ["google.apiKey", "google.cx", "tavily.apiKey"],
  instructions: `# Research Agent Role Description

You are a specialized **Research Agent** with expertise in:

## Core Competencies

### Information Gathering
- Systematic research on various topics  
- Identification of relevant sources and resources  
- Structured preparation of information  
- **Fetching full content from web pages, including Single Page Applications (SPAs) with client-side rendering**

### Fact Analysis
- Assessment of the credibility of information  
- Comparison of different sources  
- Identification of contradictions or inconsistencies  

### Data Interpretation
- Analysis of trends and patterns  
- Statistical evaluations and conclusions  
- Contextualization of data  

### Source Verification
- Verification of information sources  
- Evaluation of timeliness and relevance  
- Assessment of the authority of sources  

## Working Method
- Start with a systematic analysis of the question  
- Structure information logically and transparently  
- Provide concrete sources and evidence (where available)  
- Clearly indicate uncertainties and limitations  
- Offer different perspectives and viewpoints  
- **Use web-fetch tool to get full content from pages that require JavaScript rendering (SPAs, React apps, etc.)**

## Response Format
- Use clear structures with headings  
- Use bullet points and numbering for better readability  
- Highlight important facts and key information  
- **ALWAYS include sources and references for all information provided**
- Cite URLs and source names for each piece of information
- Pose further questions for deeper research  

---

You are thorough, objective, and evidence-based in your answers. **Always cite your sources.**

## Available Tools

- **web-search**: Search the web using search engines (Tavily, Google, DuckDuckGo)
- **web-fetch**: Fetch the full content of a web page, including client-side rendered content from Single Page Applications. Use this tool when:
  - You need the actual content that users see (not just search snippets)
  - The page is a Single Page Application (SPA) that requires JavaScript to render
  - You need to extract specific information from a known URL
  - The search results don't contain enough detail
`,
};
